import type { VideoScreenInsightSection, VideoScreenInsights } from '../../../services/api.service';
import {
  buildSourceMetaLine,
  deviationTone,
  extractKeyMetrics,
  formatCellValue,
  isDeviationColumn,
  isLabelColumn,
  isNumericColumn,
  isQuoteHighlight,
  isTotalRow,
} from './video-insights-utils';

function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderTable(section: VideoScreenInsightSection): string {
  const columns = section.columns || [];
  const rows = section.rows || [];
  if (!columns.length || !rows.length) return '';

  const head = columns.map(c => `<th>${esc(c)}</th>`).join('');
  const body = rows
    .map(row => {
      const isTotal = isTotalRow(row, columns);
      const cells = columns
        .map(col => {
          const display = formatCellValue(row[col]);
          const align = isNumericColumn(col, rows) ? ' style="text-align:right"' : '';
          let style = '';
          if (isDeviationColumn(col)) {
            const tone = deviationTone(display, col);
            if (tone === 'positive') style = ' style="color:#1c9bd7;font-weight:bold;text-align:right"';
            else if (tone === 'negative') style = ' style="color:#d64545;font-weight:bold;text-align:right"';
            else if (tone === 'neutral') style = ' style="color:#9ca3af;text-align:right"';
          }
          if (!style && align) style = align;
          if (display === '—') {
            style = ' style="color:#9ca3af;text-align:right"';
          }
          return `<td${style}>${esc(display)}</td>`;
        })
        .join('');
      const trStyle = isTotal ? ' style="background:#e4f1f9;font-weight:bold;border-top:2px solid #1c9bd7"' : '';
      return `<tr${trStyle}>${cells}</tr>`;
    })
    .join('');

  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

export function videoScreenInsightsToHtml(
  insights: VideoScreenInsights,
  meetingName?: string,
): string {
  const sections = insights.sections || [];
  if (!sections.length && !insights.glossary?.length && !insights.highlights?.length) {
    return '<p>Инсайты с видео недоступны.</p>';
  }

  const parts: string[] = [
    `<h1>Инсайты (Видео) — ${esc(meetingName || 'Встреча')}</h1>`,
    '<p class="meta">OCR с кадров Teams (Excel). Данные распознаны автоматически — возможны ошибки.</p>',
  ];

  const kpis = extractKeyMetrics(sections);
  if (kpis.length) {
    const cards = kpis
      .map(m => {
        const dev = m.deviation
          ? ` <span style="color:${m.deviationTone === 'negative' ? '#d64545' : m.deviationTone === 'positive' ? '#1c9bd7' : '#9ca3af'}">${esc(m.deviationCaption || '')} ${esc(m.deviation)}</span>`
          : '';
        return `<tr><td>${esc(m.label)}</td><td><strong>${esc(m.value)}</strong> <span style="color:#9ca3af">${esc(m.valueCaption)}</span>${dev}</td></tr>`;
      })
      .join('');
    parts.push(`<h2>Ключевые показатели</h2><table><tbody>${cards}</tbody></table>`);
  }

  const glossary = insights.glossary || [];
  const quotes = (insights.highlights || []).filter(isQuoteHighlight);
  if (glossary.length || quotes.length) {
    parts.push('<h2>Термины и цитаты</h2><div style="background:#f8fafc;padding:8pt;border:1px solid #ddd">');
    glossary.forEach(item => {
      parts.push(`<p><strong>${esc(item.term)}</strong> — ${esc(item.definition)}</p>`);
    });
    quotes.forEach(line => {
      parts.push(`<p><em>${esc(line)}</em></p>`);
    });
    parts.push('</div>');
  }

  sections.forEach(section => {
    parts.push(`<h2>${esc(section.title)}</h2>`);
    parts.push(`<p class="meta">${esc(buildSourceMetaLine(section))}</p>`);
    parts.push(renderTable(section));
    if (section.notes) {
      parts.push(`<p class="meta">${esc(section.notes)}</p>`);
    }
  });

  return parts.join('\n');
}
